/**
 * Track Manifest
 *
 * Defines all available audio tracks organized by bus type.
 * URLs point to /audio/ in public directory.
 *
 * Phase 1: CC0 / public domain placeholder tracks from OpenGameArt.org
 * Phase 3: Replace with curated bundled library
 */

// ─── Mood Music ────────────────────────────────────────────────────────

export interface MoodTrack {
  id: string;
  label: string;
  url: string;
  color: string;   // UI button color
}

export const MOOD_TRACKS: MoodTrack[] = [
  { id: 'tension',     label: 'Tension',     url: '/audio/mood/tension.mp3',     color: '#dc143c' },
  { id: 'exploration', label: 'Exploration',  url: '/audio/mood/exploration.mp3', color: '#4a90d9' },
  { id: 'combat',      label: 'Combat',       url: '/audio/mood/combat.mp3',      color: '#e25822' },
  { id: 'tavern',      label: 'Tavern',       url: '/audio/mood/tavern.mp3',      color: '#d4a017' },
  { id: 'mystery',     label: 'Mystery',      url: '/audio/mood/mystery.mp3',     color: '#8b5cf6' },
  { id: 'triumph',     label: 'Triumph',      url: '/audio/mood/triumph.mp3',     color: '#22c55e' },
  { id: 'sorrow',      label: 'Sorrow',       url: '/audio/mood/sorrow.ogg',      color: '#6b7280' },
  { id: 'dread',       label: 'Dread',        url: '/audio/mood/dread.mp3',       color: '#1e1b4b' },
];

// ─── Ambience Layers ───────────────────────────────────────────────────

export interface AmbienceLayer {
  id: string;
  label: string;
  url: string;
  icon: string;      // emoji for the tap button
  category: 'nature' | 'urban' | 'dungeon' | 'grist';
}

export const AMBIENCE_LAYERS: AmbienceLayer[] = [
  // Nature
  { id: 'rain',       label: 'Rain',         url: '/audio/ambience/rain.mp3',          icon: '🌧', category: 'nature' },
  { id: 'wind',       label: 'Wind',         url: '/audio/ambience/wind.ogg',          icon: '💨', category: 'nature' },
  { id: 'forest',     label: 'Forest',       url: '/audio/ambience/forest.mp3',        icon: '🌲', category: 'nature' },
  { id: 'night',      label: 'Night',        url: '/audio/ambience/night.mp3',         icon: '🌙', category: 'nature' },

  // Urban
  { id: 'crowd',      label: 'Crowd',        url: '/audio/ambience/crowd.mp3',         icon: '👥', category: 'urban' },
  { id: 'tavern-amb', label: 'Tavern',       url: '/audio/ambience/tavern.ogg',        icon: '🍺', category: 'urban' },
  { id: 'harbor',     label: 'Harbor',       url: '/audio/ambience/harbor.ogg',        icon: '⚓', category: 'urban' },

  // Dungeon
  { id: 'dungeon',    label: 'Dungeon',      url: '/audio/ambience/dungeon.ogg',       icon: '🕯', category: 'dungeon' },
  { id: 'cave',       label: 'Cave',         url: '/audio/ambience/cave.flac',         icon: '🦇', category: 'dungeon' },

  // Grist-specific
  { id: 'tether-hum', label: 'Tether Hum',   url: '/audio/ambience/tether-hum.ogg',    icon: '⚡', category: 'grist' },
  { id: 'scar-whisp', label: 'Scar Whispers',url: '/audio/ambience/scar-whispers.ogg', icon: '🌀', category: 'grist' },
  { id: 'foundry',    label: 'Foundry',      url: '/audio/ambience/foundry.ogg',       icon: '🔨', category: 'grist' },
];

// ─── Sound Effects ─────────────────────────────────────────────────────

export interface SFXEntry {
  id: string;
  label: string;
  url: string;
  icon: string;      // emoji for the tap button
  category: 'combat' | 'environment' | 'social' | 'magic';
}

export const SFX_ENTRIES: SFXEntry[] = [
  // Combat
  { id: 'sword-clash',  label: 'Clash',    url: '/audio/sfx/sword-clash.ogg',    icon: '⚔️',  category: 'combat' },
  { id: 'arrow-loose',  label: 'Arrow',    url: '/audio/sfx/arrow-loose.ogg',    icon: '🏹',  category: 'combat' },
  { id: 'shield-block', label: 'Shield',   url: '/audio/sfx/shield-block.ogg',   icon: '🛡',  category: 'combat' },
  { id: 'critical-hit', label: 'Crit!',    url: '/audio/sfx/critical-hit.ogg',   icon: '💥',  category: 'combat' },
  { id: 'death-cry',    label: 'Death',    url: '/audio/sfx/death-cry.ogg',      icon: '💀',  category: 'combat' },

  // Environment
  { id: 'door-creak',   label: 'Door',     url: '/audio/sfx/door-creak.ogg',     icon: '🚪',  category: 'environment' },
  { id: 'chest-open',   label: 'Chest',    url: '/audio/sfx/chest-open.ogg',     icon: '📦',  category: 'environment' },
  { id: 'trap-spring',  label: 'Trap',     url: '/audio/sfx/trap-spring.ogg',    icon: '⚙️',  category: 'environment' },

  // Social
  { id: 'coin-purse',   label: 'Coins',    url: '/audio/sfx/coin-purse.ogg',     icon: '💰',  category: 'social' },
  { id: 'crowd-gasp',   label: 'Gasp',     url: '/audio/sfx/crowd-gasp.wav',     icon: '😮',  category: 'social' },
  { id: 'dramatic-sting',label: 'Sting',   url: '/audio/sfx/dramatic-sting.ogg', icon: '🎵',  category: 'social' },

  // Magic
  { id: 'arcane-hum',   label: 'Arcane',   url: '/audio/sfx/arcane-hum.ogg',     icon: '✨',  category: 'magic' },
  { id: 'divine-chime', label: 'Divine',   url: '/audio/sfx/divine-chime.ogg',   icon: '🔔',  category: 'magic' },
  { id: 'fireball',     label: 'Fireball', url: '/audio/sfx/fireball.ogg',       icon: '🔥',  category: 'magic' },
  { id: 'teleport',     label: 'Teleport', url: '/audio/sfx/teleport.ogg',       icon: '🌀',  category: 'magic' },
];
